import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileText, Building2, TrendingUp, ScrollText, ArrowRight, CheckCircle2, Users, Award, Clock, X } from 'lucide-react';
import { motion } from 'framer-motion';

export const Home = () => {
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('tax_popup_dismissed');
    if (dismissed) return;
    const timer = setTimeout(() => setShowPopup(true), 30000);
    return () => clearTimeout(timer);
  }, []);

  const dismissPopup = () => {
    setShowPopup(false);
    sessionStorage.setItem('tax_popup_dismissed', 'true');
  };

  const quickServices = [
    { title: 'File Your ITR', icon: FileText, link: '/services', color: 'bg-secondary' },
    { title: 'GST Registration', icon: Building2, link: '/services', color: 'bg-primary' },
    { title: 'Financial Planning', icon: TrendingUp, link: '/services', color: 'bg-accent' },
    { title: 'Contact Us', icon: ScrollText, link: '/contact', color: 'bg-secondary' },
  ];

  const stats = [
    { icon: Clock, value: '38+', label: 'Years Experience' },
    { icon: Users, value: '1000+', label: 'Happy Clients' },
    { icon: Award, value: '10000+', label: 'Tax Returns Filed' },
    { icon: CheckCircle2, value: '100%', label: 'Client Satisfaction' },
  ];

  const services = [
    {
      title: 'Income Tax Services',
      description: 'Complete ITR filing, tax planning, and compliance solutions',
      features: ['ITR Filing', 'Tax Notices', 'PAN Services', 'Scrutiny Handling']
    },
    {
      title: 'GST Services',
      description: 'GST registration, filing, and comprehensive GST solutions',
      features: ['GST Registration', 'GST Returns', 'GST Amendments', 'GST Compliance']
    },
    {
      title: 'Financial Planning',
      description: 'Personalized financial planning for your future goals',
      features: ['Financial Blueprint', 'Insurance', 'Investment Planning', 'E-Wealth Account']
    },
    {
      title: 'Estate Planning',
      description: 'Secure your legacy with professional estate planning',
      features: ['Will Drafting', 'Registered Will', 'Trust Formation', 'Succession Planning']
    },
  ];

  const testimonials = [
    { name: 'Padma Joshi Samvedi', text: 'I got my ITR filed by Jainesh Sir and had a great experience. He is very helpful, clears all doubts patiently, and makes the whole process easy and hassle-free. Definitely recommended.', rating: 5 },
    { name: 'Karan Singh Rathore', text: 'Good services', rating: 5 },
    { name: 'The Fashion Aesthetic', text: 'You can definitely count on them!', rating: 5 },
    { name: 'Honey Jain', text: 'I had a great experience working with him as my financial advisor. He explains everything clearly and provides practical, well-thought-out guidance based on my financial goals.', rating: 5 },
  ];

  return (
    <div className="bg-background" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative py-24 md:py-32 overflow-hidden" 
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1758691736975-9f7f643d178e?w=1600&q=80)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-hero-gradient"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4" data-testid="hero-subtitle">
                Established 1986
              </p>
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight text-white font-playfair mb-6" data-testid="hero-title">
                Tax Consultant & Financial Advisor in Indore
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-white/90 mb-8" data-testid="hero-description">
                Professional Tax & Financial Advisory Services in Indore. Trust our 38+ years of expertise for Income Tax, GST, and Comprehensive Financial Planning.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white" data-testid="hero-services-button">
                    Our Services
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="hero-contact-button">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Service Buttons */}
      <section className="py-16 -mt-12 relative z-20" data-testid="quick-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickServices.map((service, index) => (
              <Link key={index} to={service.link}>
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer" data-testid={`quick-service-${index}`}>
                  <CardContent className="p-6">
                    <div className={`${service.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <service.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">{service.title}</h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary text-primary-foreground" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <stat.icon className="h-10 w-10 mx-auto mb-4 text-secondary" />
                <div className="text-4xl font-bold font-playfair mb-2">{stat.value}</div>
                <div className="text-sm opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">About Us</p>
              <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-playfair mb-6" data-testid="about-title">
                38+ Years of Trust & Excellence
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Jay Kothari and Company has been serving Indore since 1986, providing professional tax and financial advisory services. Founded by Jay Kothari, a distinguished tax consultant with degrees in Commerce and Law from DAVV, our firm has grown to become a trusted name in financial services.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Our team includes Jainesh Kothari, a Certified Financial Planner® and Chartered Trust and Estate Planner®, and Abhijeet Kothari, a NISM-certified financial expert. Together, we bring decades of combined experience in Income Tax, GST, Personal Financial Planning, and Estate Planning.
              </p>
              <Link to="/partners">
                <Button variant="outline" data-testid="meet-team-button">
                  Meet Our Team
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_kothari-services/artifacts/is1fjxoi_2025-07-11.jpg.jpeg"
                alt="Jay Kothari at work"
                className="rounded-xl shadow-xl w-full h-64 object-cover"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_kothari-services/artifacts/lambkl8j_2025-06-22.jpg.jpeg"
                alt="Team consultation"
                className="rounded-xl shadow-xl w-full h-64 object-cover mt-8"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_kothari-services/artifacts/r4iuqoiq_2025-12-12%20%282%29.jpg.jpeg"
                alt="Office environment"
                className="rounded-xl shadow-xl w-full h-64 object-cover -mt-8"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_kothari-services/artifacts/mxv5mlof_2025-12-12%20%281%29.jpg.jpeg"
                alt="Professional workspace"
                className="rounded-xl shadow-xl w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-24 md:py-32 bg-muted/20" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Our Services</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-playfair" data-testid="services-title">
              Tax, GST & Estate, Financial Planning Services in Indore
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const isEstatePlanning = service.title === 'Estate Planning';
              const Wrapper = isEstatePlanning ? 'a' : 'div';
              const wrapperProps = isEstatePlanning ? { href: '/services#estate-planning', onClick: (e) => { e.preventDefault(); window.location.href = '/services#estate-planning'; } } : {};
              return (
              <Wrapper key={index} {...wrapperProps} className={isEstatePlanning ? 'block' : ''}>
              <Card className={`group hover:shadow-xl transition-all duration-500 hover:border-primary/20 h-full ${isEstatePlanning ? 'cursor-pointer' : ''}`} data-testid={`service-card-${index}`}>
                <CardContent className="p-8">
                  <h3 className="text-xl md:text-2xl font-medium font-playfair mb-3">{service.title}</h3>
                  <p className="text-base leading-relaxed text-muted-foreground mb-6">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle2 className="h-4 w-4 text-secondary mr-2 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {isEstatePlanning && (
                    <div className="mt-6">
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-secondary group-hover:underline">
                        View Details & Book Appointment
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
              </Wrapper>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" data-testid="view-all-services-button">
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-24 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Testimonials</p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-playfair" data-testid="testimonials-title">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300" data-testid={`testimonial-${index}`}>
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-accent text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 italic">"{testimonial.text}"</p>
                  <p className="font-semibold text-sm">- {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button variant="outline" data-testid="view-all-testimonials-button">
                View All Testimonials
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Financial Tools Preview */}
      <section className="py-24 md:py-32 bg-muted/20" data-testid="tools-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-medium tracking-wide uppercase text-secondary mb-4">Financial Tools</p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-playfair mb-6" data-testid="tools-title">
            Smart Calculators for Your Financial Planning
          </h2>
          <p className="text-base leading-relaxed text-muted-foreground mb-8 max-w-2xl mx-auto">
            Access our suite of 14 interactive financial calculators to plan your investments, taxes, and future goals.
          </p>
          <Link to="/financial-tools">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" data-testid="explore-tools-button">
              Explore Financial Tools
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight font-playfair mb-6" data-testid="cta-title">
            Ready to Secure Your Financial Future?
          </h2>
          <p className="text-lg leading-relaxed mb-8 opacity-90">
            Let our experienced team guide you through tax planning, GST compliance, and comprehensive financial advisory services.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" variant="secondary" data-testid="cta-contact-button">
                Get in Touch
              </Button>
            </Link>
            <a href="tel:+919406563535">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="cta-call-button">
                Call: +91 94065 63535
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Tax Filing Season Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="tax-popup-overlay" onClick={dismissPopup}>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-testid="tax-popup">
            <div className="bg-gradient-to-r from-primary to-primary/80 p-6 text-white text-center">
              <FileText className="h-10 w-10 mx-auto mb-3 opacity-90" />
              <h2 className="text-2xl font-bold font-playfair">TAX FILING SEASON IS HERE</h2>
            </div>
            <div className="p-6 text-center">
              <p className="text-lg font-semibold text-foreground mb-2">
                File before <span className="text-secondary">30th June 2026</span>
              </p>
              <p className="text-base text-secondary font-bold mb-4">and get Early Bird Discount!</p>
              <p className="text-sm text-muted-foreground mb-6">
                Don't wait till the last minute. Our expert team ensures accurate and hassle-free ITR filing.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/services" onClick={dismissPopup}>
                  <Button className="bg-secondary hover:bg-secondary/90" data-testid="popup-file-now-btn">
                    File Now <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                </Link>
                <Button variant="outline" onClick={dismissPopup} data-testid="popup-dismiss-btn">
                  Maybe Later
                </Button>
              </div>
            </div>
            <button onClick={dismissPopup} className="absolute top-3 right-3 text-white/80 hover:text-white" data-testid="popup-close-btn">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
